import { format } from 'date-fns';
import { curveCardinal } from 'd3-shape';
import {
  Area,
  AreaChart,
  Brush,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  TooltipContentProps,
  XAxis,
  YAxis,
} from 'recharts';
import { Button } from '@/components/ui/button.tsx';
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { useGetPlantControlPanelOverviewQuery } from '@/features/plants/hooks/queries/[plantId]/use-get-plant-control-panel-overview.ts';
import { DashboardCard } from '@/components/ui/dashboard-card.tsx';
import { LastUpdateTimestamp } from '@/components/last-update-timestamp.tsx';
import { cn } from '@/utils/utils.ts';
import { useMediaQuery } from '@/hooks/use-media-query.tsx';
import { useBrushSelection } from '@/hooks/use-brush-selection.tsx';
import { useTranslation } from 'react-i18next';
import type {
  NameType,
  Payload,
  ValueType,
} from 'recharts/types/component/DefaultTooltipContent';
import {
  getDeviceTypeNameByCode,
  getDeviceFieldByFieldId,
} from '@/features/devices/schemas/devices.metadata.ts';
import { DeviceTypeCodes } from '@/features/devices/schemas/devices.schemas.ts';

const cardinal = curveCardinal.tension(0.2);

const CHART_COLORS = [
  '#005780',
  '#A75161',
  '#AC99FB',
  '#4CAF50',
  '#FF9800',
  '#9C27B0',
  '#00BCD4',
];

type ChartMetric = {
  label: string;
  color: string;
  selected: boolean;
  dataKey: string;
  unit: string;
  id: string;
};

export const PlantControlChart = ({
  isLoading,
  className,
}: {
  isLoading?: boolean;
  className?: string;
}) => {
  const { t } = useTranslation();
  const { plantId } = useParams();
  const isMobile = useMediaQuery('(max-width:640px)');

  const [fields, setFields] = useState<ChartMetric[]>([]);

  const {
    data: controlPanel,
    isLoading: isPlantControlPanelLoading,
    dataUpdatedAt: dataUpdatedAt,
  } = useGetPlantControlPanelOverviewQuery({
    plantId: plantId || '',
  });
  const { chart } = controlPanel || {};
  const isDataAvailable = chart?.length ?? 0;

  const { brushState, handleBrushChange } = useBrushSelection(chart);

  const chartMetrics = useMemo(() => {
    if (!chart || chart.length === 0) return [];

    const firstDataPoint = chart[0];
    const dataKeys = Object.keys(firstDataPoint).filter((key) => key !== 'time');

    return dataKeys.map((key, index) => {
      const parts = key.split(':');

      if (parts.length === 2) {
        const deviceTypeCode = Number(parts[0]) as DeviceTypeCodes;
        const fieldId = Number(parts[1]);

        const deviceTypeName = getDeviceTypeNameByCode(deviceTypeCode);
        const fieldMetadata = getDeviceFieldByFieldId(deviceTypeName, fieldId);

        if (fieldMetadata) {
          return {
            label: `${deviceTypeName}: ${fieldMetadata.name}`,
            color: CHART_COLORS[index % CHART_COLORS.length],
            selected: true,
            dataKey: key,
            unit: fieldMetadata.unit || '',
            id: key.replace(':', '_'),
          };
        }
      }

      return {
        label: key,
        color: CHART_COLORS[index % CHART_COLORS.length],
        selected: true,
        dataKey: key,
        unit: '',
        id: key.replace(':', '_'),
      };
    });
  }, [chart]);

  useEffect(() => {
    if (chartMetrics.length > 0) {
      setFields((currentFields) => {
        if (currentFields.length === 0) {
          return chartMetrics;
        }

        return chartMetrics.map((metric) => {
          const existingField = currentFields.find((f) => f.dataKey === metric.dataKey);
          return existingField ? { ...metric, selected: existingField.selected } : metric;
        });
      });
    }
  }, [chartMetrics]);

  return (
    <DashboardCard
      className={cn('', className)}
      isLoading={isPlantControlPanelLoading || isLoading}
    >
      <div className="flex h-full flex-col justify-between gap-4">
        <div className="flex flex-col gap-2">
          <h3 className="font-semibold text-nowrap">Energy management</h3>
          <LastUpdateTimestamp updatedAt={dataUpdatedAt} />
        </div>

        {isDataAvailable ? (
          <div className="flex flex-col gap-2">
            <div className="relative h-[380px] w-full">
              <div className="absolute top-0 left-0 h-full w-full space-y-2">
                <ResponsiveContainer>
                  <AreaChart
                    data={chart}
                    margin={{
                      top: 0,
                      right: 0,
                      bottom: 0,
                      left: isMobile ? -15 : -5,
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      xAxisId="main"
                      dataKey="time"
                      minTickGap={60}
                      style={{ fontSize: isMobile ? 12 : 16 }}
                      tickFormatter={(t) => format(t, 'HH:mm:ss')}
                    />
                    <YAxis
                      yAxisId="main"
                      style={{ fontSize: isMobile ? 12 : 16, userSelect: 'none' }}
                    />
                    <Tooltip
                      cursor={false}
                      content={(props) => (
                        <ChartTooltipComponent {...props} fields={fields} />
                      )}
                    />

                    <Brush
                      dataKey="time"
                      height={18}
                      startIndex={brushState.startIndex}
                      endIndex={brushState.endIndex}
                      stroke="hsl(var(--chart-1))"
                      fill="hsl(var(--chart-5))"
                      tickFormatter={(value) => {
                        if (!value) return '';
                        return format(value, 'HH:mm:ss');
                      }}
                      onChange={handleBrushChange}
                    ></Brush>

                    {fields.map((v) => {
                      if (!v.selected) return null;

                      return (
                        <Area
                          xAxisId="main"
                          yAxisId="main"
                          name={v.label}
                          key={v.id}
                          type={cardinal}
                          dataKey={v.dataKey}
                          stroke={v.color}
                          fill={`url(#${v.id})`}
                          fillOpacity={0.3}
                        />
                      );
                    })}
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className="mx-auto flex flex-wrap gap-1 md:gap-4">
              {fields.map((f) => (
                <Button
                  key={f.dataKey}
                  variant="ghost"
                  style={{ opacity: !f.selected ? '0.4' : '1' }}
                  className="h-min w-min justify-start p-1 px-2"
                  onClick={() => {
                    setFields((current) =>
                      current.map((c) => ({
                        ...c,
                        selected: c.label === f.label ? !c.selected : c.selected,
                      }))
                    );
                  }}
                >
                  <div className="flex items-center gap-2">
                    <span
                      className="size-4 rounded-full"
                      style={{ backgroundColor: f.color }}
                    />
                    <span className="text-start text-sm font-medium text-nowrap text-[#7D7D85]">
                      {f.label}
                    </span>
                  </div>
                </Button>
              ))}
            </div>
          </div>
        ) : (
          <div className="flex h-full min-h-[238px] w-full items-center justify-center rounded-md bg-gray-50">
            <span className="text-muted-foreground">
              {t('devices.batteryModule.noDataFound')}
            </span>
          </div>
        )}
      </div>
    </DashboardCard>
  );
};

const ChartTooltipComponent = ({
  payload,
  label,
  fields,
}: TooltipContentProps<ValueType, NameType> & {
  fields: ChartMetric[];
}) => {
  const formattedDate = label ? format(new Date(label), 'HH:mm:ss') : '';

  const formatValue = (p: Payload<ValueType, NameType>): string => {
    if (!p?.value) return '0';

    const field = fields.find((f) => f.dataKey === p.dataKey);
    const value = Number(p.value);
    const fixedValue = value.toFixed(2);
    const unit = field?.unit || '';

    return unit ? `${fixedValue} ${unit}` : fixedValue;
  };

  const sortedPayload = payload?.slice().sort((a: Payload<ValueType, NameType>, b: Payload<ValueType, NameType>) => {
    const nameA = a.name?.toString() || '';
    const nameB = b.name?.toString() || '';
    return nameA.localeCompare(nameB);
  });

  return (
    <div className="flex flex-col border border-gray-300 bg-white p-4 text-base">
      <p>{formattedDate}</p>
      <div className="mt-2">
        {sortedPayload?.map((p: Payload<ValueType, NameType>) => (
          <p style={{ color: p.stroke }} key={p.name}>
            {p.name}: {formatValue(p)}
          </p>
        ))}
      </div>
    </div>
  );
};
